import type { RefObject } from 'preact';
import { type MutableRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';

import useDropdownNav from './dropdownNav';

const hiddenClass = 'cm_hide';

function addListener<E extends Event>(
  target: Element | Document,
  event: string,
  handler: (event: E) => void,
  options?: AddEventListenerOptions | boolean,
): () => void {
  const listener = handler as EventListener;
  target.addEventListener(event, listener, options);
  return () => target.removeEventListener(event, listener, options);
}

interface DropdownOptions {
  dropdownName?: string;
  toggle?: boolean;
  useClick?: boolean;
}

interface DropdownResult {
  keyboardHandler: (e: KeyboardEvent) => void;
  resetNav: () => void;
  hideDropdown: () => void;
  showDropdown: () => void;
  DO_NOT_USE_visible: boolean;
}

export default function useDropdown(
  rootRef: RefObject<Element>,
  focusSelector: string,
  { dropdownName = 'dropdown', toggle = false, useClick = false }: DropdownOptions = {},
): DropdownResult {
  const focusRef = useRef<Element | null>(null);
  const toggleRef = useRef<Element | null>(null);
  const dropdownRef = useRef<Element | null>(null);

  const [visible, setVisibility] = useState(false);

  const navCallbacks = useDropdownNav(rootRef, setVisibility);

  // Intentionally runs on every render: refs may be attached to DOM nodes
  // mutated by the consumer's template between renders. Syncing refs each
  // pass is cheaper than teaching callers to thread ref identities.
  useLayoutEffect(function setRefs() {
    const root = rootRef.current;
    if (root) {
      trySetRef(root, focusSelector, focusRef, 'focusable');
      if (focusRef.current instanceof HTMLElement) {
        focusRef.current.tabIndex = -1;
      }

      trySetRef(root, `.cm_${dropdownName}`, dropdownRef, 'dropdown');

      if (toggle) {
        trySetRef(root, '.cm_toggle-dropdown', toggleRef, 'toggle');
      }
    }
  });

  useLayoutEffect(function visibilityAndHiddenClassSync() {
    if (rootRef.current) {
      const currentlyVisible = !dropdownRef.current?.classList.contains(hiddenClass);
      if (currentlyVisible !== visible) {
        dropdownRef.current?.classList.toggle(hiddenClass, !visible);
      }
    }
  });

  useEffect(function addEventListeners() {
    if (rootRef.current) {
      const cleanups: Array<() => void> = [];

      if (toggle && toggleRef.current) {
        cleanups.push(
          addListener(toggleRef.current, 'pointerdown', (e: PointerEvent) => {
            // ignore non-primary buttons (right/middle click)
            if (e.button && e.button !== 0) {
              return;
            }

            e.preventDefault();
            const willOpen = !visible;
            setVisibility(willOpen);
            if (willOpen && focusRef.current && focusRef.current !== window.document.activeElement) {
              // focus asynchronously so native focus/blur settle
              setTimeout(() => {
                if (focusRef.current instanceof HTMLElement) {
                  focusRef.current.focus();
                }
              }, 0);
            }
          }),
        );
      }

      if (!toggle && focusRef.current) {
        cleanups.push(addListener(focusRef.current, 'focusin', () => setVisibility(true)));
      }

      cleanups.push(
        addListener(
          rootRef.current,
          'keydown',
          ({ key }: KeyboardEvent) => key === 'Escape' && setVisibility(false),
        ),
      );

      if (!useClick && focusRef.current) {
        cleanups.push(
          addListener(focusRef.current, 'focusout', ({ target, relatedTarget }: FocusEvent) => {
            setVisibility(
              target instanceof HTMLSelectElement && !relatedTarget
                ? true
                : relatedTarget instanceof Node && !!focusRef.current?.contains(relatedTarget),
            );
          }),
        );
      }

      // listen pointerdown to detect outside clicks/touches reliably
      cleanups.push(
        addListener(window.document, 'pointerdown', ({ button, target }: PointerEvent) => {
          // ignore non-primary buttons (right/middle click)
          if (button && button !== 0) {
            return;
          }

          // if click/touch is inside the focusable area, dropdown, or toggle, do nothing
          const isTargetNode = target instanceof Node;
          const isInFocus = isTargetNode && focusRef.current && focusRef.current.contains(target);
          const isPointedInDropdown =
            isTargetNode && dropdownRef.current && dropdownRef.current.contains(target);
          const isPointedInToggle = isTargetNode && toggleRef.current && toggleRef.current.contains(target);

          if (!isInFocus && !isPointedInDropdown && !isPointedInToggle) {
            setVisibility(false);
          }
        }),
      );

      return () => cleanups.forEach((cleanup) => cleanup());
    }
  });

  const hideDropdown = useCallback(() => setVisibility(false), []);
  const showDropdown = useCallback(() => setVisibility(true), []);
  // eslint-disable-next-line camelcase
  return { ...navCallbacks, hideDropdown, showDropdown, DO_NOT_USE_visible: visible };
}

function trySetRef(root: Element, selector: string, ref: MutableRef<Element | null>, name: string): void {
  const elem = root.matches(selector) ? root : root.querySelector(selector);

  if (ref.current !== elem) {
    ref.current = elem;
  }
  if (!elem) {
    // eslint-disable-next-line no-console
    console.log(`Can't find ${name} element with ${selector} selector within root element`, root);
  }
}
